import { useParams } from 'react-router';
import type { AuditEvent } from '@gatecheckpro/contracts';
import { Card, EmptyState, LoadingRegion } from '@gatecheckpro/design-system';
import { useResource } from '../lib/useResource.ts';
import { FailureNotice } from '../lib/refusal.tsx';

/**
 * The organizer's own audit trail.
 *
 * Scoped to this tenant by the server, not by a filter this page passes. An
 * organizer being able to see who changed what, when, is part of the product
 * rather than a compliance afterthought (SDAD Section 39).
 */
const ACTION_LABELS: Readonly<Record<string, string>> = {
  'organization.created': 'Organization created',
  'organization.profile_updated': 'Profile updated',
  'organization.member_invited': 'Member invited',
  'organization.member_activated': 'Member joined',
  'organization.member_removed': 'Member removed',
  'organization.roles_changed': 'Roles changed',
  'organization.verification_submitted': 'Verification submitted',
  'organization.verification_decided': 'Verification decided',
  'ops.organization_reviewed': 'Reviewed by CABS',
};

export function ActivityPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { resource } = useResource<{ events: AuditEvent[] }>(
    organizationId === undefined
      ? null
      : `/api/v1/organizer/organizations/${organizationId}/audit?limit=100`,
  );

  if (resource.status === 'loading') return <LoadingRegion label="Loading activity" />;
  if (resource.status === 'failed') return <FailureNotice error={resource.error} />;

  if (resource.data.events.length === 0) {
    return (
      <EmptyState title="No activity recorded yet">
        Consequential changes appear here as they happen — who did what, when, and why.
      </EmptyState>
    );
  }

  return (
    <Card title="Activity" eyebrow="Append-only" flush>
      <div className="gc-table-scroll" style={{ border: 0, borderRadius: 0 }}>
        <table className="gc-table">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">What</th>
              <th scope="col">Who</th>
              <th scope="col">Reason</th>
            </tr>
          </thead>
          <tbody>
            {resource.data.events.map((event) => (
              <tr key={event.auditEventId}>
                <td className="gc-mono">{new Date(event.occurredAt).toLocaleString()}</td>
                <td>
                  <div className="gc-stack gc-stack--tight">
                    <strong>{ACTION_LABELS[event.action] ?? event.action}</strong>
                    <span className="gc-muted gc-mono">{event.resourceType}</span>
                  </div>
                </td>
                <td>{event.actorDisplayName ?? '—'}</td>
                <td className="gc-muted">{event.reasonCode ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
